import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TruckIcon, ClipboardCheck, Brain, CircleCheck, ChevronRight, Info,
} from 'lucide-react';
import { deliveryQualityService } from 'services/deliveryQualityService';
import { Card } from '../../../../components/ui/Card';
import { Skeleton } from '../../../../components/ui/Skeleton';
import { cn } from '../../../../utils/cn';
import { formatNumberCL, formatPercent as fmtPct } from '../../../../utils/formatters';

/**
 * Embudo de calidad: muestra el funnel
 *
 *   Entregados -> Evaluados -> Con IA -> IA validada
 *
 * para el mismo (date_from, date_to, company_id|logistic_company_id) que
 * usa el resto del tab Global. Sirve como reconciliacion visual entre
 * "Todos los pedidos" (entregados), "Calidad de entregas" (evaluaciones)
 * y "Salud IA" (jobs IA), explicando donde se pierde cobertura.
 */
const fmtNum = (n) => (typeof n === 'number' ? formatNumberCL(n) : '—');

// Chip tonal por etapa, via tokens semánticos (dark mode automático).
const toneChip = {
  info: 'bg-status-info-soft text-status-info',
  brand: 'bg-brand-50 text-brand-600',
  success: 'bg-status-success-soft text-status-success',
};

const Stage = ({ def, value, refValue, idx }) => {
  const Icon = def.icon;
  const rate = def.rateOf && refValue ? value / refValue : null;
  return (
    <div
      className={cn(
        'flex-1 min-w-[150px] flex flex-col gap-1 p-3.5 rounded-[10px]',
        'bg-bg border border-border transition-shadow duration-200 hover:shadow-md',
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            'w-7 h-7 rounded-lg flex items-center justify-center shrink-0',
            toneChip[def.tone] ?? toneChip.info,
          )}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
        <div className="text-xs text-fg-muted font-medium flex items-center gap-1.5">
          {idx + 1}. {def.label}
          <span title={def.tooltip} className="cursor-help text-fg-subtle">
            <Info className="w-3 h-3" />
          </span>
        </div>
      </div>
      <div className="text-[26px] font-bold text-fg leading-tight tabular-nums">
        {fmtNum(value)}
      </div>
      {rate !== null && (
        <div className="text-[11px] text-fg-subtle">
          {fmtPct(rate)} {def.rateLabel}
        </div>
      )}
    </div>
  );
};

const Connector = () => (
  <div aria-hidden className="w-[22px] flex items-center justify-center text-fg-subtle shrink-0">
    <ChevronRight className="w-3.5 h-3.5 opacity-60" />
  </div>
);

const QualityFunnel = ({ filters }) => {
  const { t } = useTranslation('deliveryquality');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const STAGE_DEFS = [
    {
      key: 'delivered',
      label: t('Entregados'),
      tone: 'info',
      icon: TruckIcon,
      tooltip: t('Paquetes con status=4 entregados en el rango (filtrado por la empresa del usuario y por fecha de entrega real). Equivalente a la card "Paquetes entregados" de "Todos los pedidos".'),
      rateOf: null,
    },
    {
      key: 'evaluated',
      label: t('Evaluados'),
      tone: 'brand',
      icon: ClipboardCheck,
      tooltip: t('Paquetes UNICOS con score en delivery_quality_scores en el mismo rango. Equivale al universo de "Calidad de entregas -> Global", pero contando paquetes unicos (no evaluaciones duplicadas).'),
      rateOf: 'delivered',
      rateLabel: t('cobertura'),
    },
    {
      key: 'with_ai',
      label: t('Con IA'),
      tone: 'info',
      icon: Brain,
      tooltip: t('Paquetes evaluados que ademas tienen un veredicto del analisis IA externo (calidad-ia). El resto se evaluo solo con criterios deterministas (RUT, distancia, puntualidad, etc).'),
      rateOf: 'evaluated',
      rateLabel: t('con IA'),
    },
    {
      key: 'ai_validated',
      label: t('IA validada'),
      tone: 'success',
      icon: CircleCheck,
      tooltip: t('Veredicto "ENTREGA VALIDADA" del analisis IA. El resto se reparte entre "ENTREGA DUDOSA" y "ENTREGA RECHAZADA".'),
      rateOf: 'with_ai',
      rateLabel: t('aprobacion IA'),
    },
  ];

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    deliveryQualityService
      .getCoverage(filters)
      .then((r) => {
        if (alive) setData(r.data);
      })
      .catch((e) => {
        if (alive) setError(e?.message || 'Error');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [filters]);

  const valuesByKey = useMemo(() => {
    if (!data) return {};
    return {
      delivered: data.delivered,
      evaluated: data.evaluated,
      with_ai: data.with_ai,
      ai_validated: data.ai_validated,
    };
  }, [data]);

  const headerHint = useMemo(() => {
    if (!data) return null;
    const drift = data.pending_evaluation;
    if (!drift) return t('Cobertura completa: cada entregado tiene evaluacion.');
    return `${fmtNum(drift)} ${t('entregados aun sin evaluar')} (${fmtPct(
      data.delivered ? drift / data.delivered : 0,
    )} ${t('del total')}).`;
  }, [data, t]);

  return (
    <Card padding="md" className="mb-3">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-fg">{t('Embudo de calidad')}</div>
          <div className="text-xs text-fg-subtle">
            {error
              ? t('No se pudo cargar el embudo (mostrando KPIs sin reconciliacion).')
              : headerHint || t('Reconciliacion entre paquetes entregados y evaluaciones.')}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-stretch gap-2">
          {STAGE_DEFS.map((d) => (
            <React.Fragment key={d.key}>
              <Skeleton className="flex-1 min-w-[140px] h-24 rounded-[10px]" />
              {d.key !== 'ai_validated' && <Connector />}
            </React.Fragment>
          ))}
        </div>
      ) : data ? (
        <div className="flex items-stretch flex-wrap gap-2">
          {STAGE_DEFS.map((def, idx) => (
            <React.Fragment key={def.key}>
              <Stage
                def={def}
                idx={idx}
                value={valuesByKey[def.key]}
                refValue={def.rateOf ? valuesByKey[def.rateOf] : null}
              />
              {idx < STAGE_DEFS.length - 1 && <Connector />}
            </React.Fragment>
          ))}
        </div>
      ) : null}
    </Card>
  );
};

export default QualityFunnel;
